import { Box, Button, Grid } from "@material-ui/core";
import {
    allPenaltiesTypes,
    Collections,
    Infraction,
    InfractionDefaultValues,
    PenaltyDescriptionByType,
    PenaltyType,
    useCollection,
    useCurrentSeason,
    useOnSubmitFormik,
} from "api";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import {
    BoxCard,
    InputFieldFormik,
    PageLayout,
    SelectFieldFormik,
    SelectOption,
    SelectSeason,
    SubmitButton,
    yupMaxLengthField,
    yupMinLengthField,
    yupMinValueField,
    yupRequiredField,
} from "ui/componentes";
import * as Yup from "yup";
import { useHistory, useParams } from "react-router";

const InfractionValidation = Yup.object().shape({
    seasonId: Yup.string().required(yupRequiredField),
    key: Yup.string().required(yupRequiredField).max(10, yupMaxLengthField),
    description: Yup.string()
        .min(10, yupMinLengthField)
        .max(400, yupMaxLengthField)
        .required(yupRequiredField),
    penaltyType: Yup.string()
        .required(yupRequiredField)
        .oneOf(allPenaltiesTypes),
    stopAndGoTime: Yup.number().when("penaltyType", {
        is: (value) => PenaltyType.STOP_AND_GO === value,
        then: Yup.number().required(yupRequiredField).min(1, yupMinValueField),
    }),
    addTimeToEnd: Yup.number().when("penaltyType", {
        is: (value) => PenaltyType.ADD_TIME_TO_END === value,
        then: Yup.number().required(yupRequiredField).min(1, yupMinValueField),
    }),
    penaltyPointsAppliedAfter: Yup.number()
        .required(yupRequiredField)
        .min(1, yupMinValueField),
});

const penaltyOptions: SelectOption[] = [
    {
        label: PenaltyDescriptionByType[PenaltyType.DISABLE_CHAT],
        value: PenaltyType.DISABLE_CHAT,
    },
    {
        label: PenaltyDescriptionByType[PenaltyType.DRIVE_THROUGH],
        value: PenaltyType.DRIVE_THROUGH,
    },
    {
        label: PenaltyDescriptionByType[PenaltyType.STOP_AND_GO],
        value: PenaltyType.STOP_AND_GO,
    },
    {
        label: PenaltyDescriptionByType[PenaltyType.ADD_TIME_TO_END],
        value: PenaltyType.ADD_TIME_TO_END,
    },
    {
        label: PenaltyDescriptionByType[PenaltyType.DISQUALIFY],
        value: PenaltyType.DISQUALIFY,
    },
    {
        label: PenaltyDescriptionByType[PenaltyType.REMOVE],
        value: PenaltyType.REMOVE,
    },
    {
        label: PenaltyDescriptionByType[PenaltyType.WARNING],
        value: PenaltyType.WARNING,
    },
];

const InfractionForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const currentSeason = useCurrentSeason() || {};
    const onSubmit = useOnSubmitFormik<Infraction>(Collections.INFRACTIONS, {
        callback: () => history.push(`/infractions`),
    });

    const infraction: Infraction =
        useCollection<Infraction>({
            collection: Collections.INFRACTIONS,
            doc: id || "-1",
        }) || InfractionDefaultValues;

    const [initialValues, setInitialValues] = useState<Infraction>({
        ...infraction,
    });
    useEffect(() => {
        setInitialValues((old) => ({ ...old, ...infraction }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [infraction.id]);

    useEffect(() => {
        if (!initialValues.seasonId) {
            setInitialValues((old) => ({
                ...old,
                seasonId: currentSeason.id,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSeason.id, initialValues.seasonId]);

    return (
        <PageLayout
            title="Configuração de infrações"
            boxCardProps={{
                display: "flex",
                flexDirection: "column",
            }}
            showInBoxCard={false}
        >
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <BoxCard>
                        <Formik
                            enableReinitialize={true}
                            initialValues={initialValues}
                            validationSchema={InfractionValidation}
                            onSubmit={onSubmit}
                        >
                            {({ values }) => (
                                <Form>
                                    <Grid container spacing={1}>
                                        <Grid item md={3} xs={12}>
                                            <SelectSeason />
                                        </Grid>
                                        <Grid item md={3} xs={12}>
                                            <InputFieldFormik
                                                id="key"
                                                name="key"
                                                label="Item"
                                            />
                                        </Grid>
                                        <Grid item md={3} xs={12}>
                                            <SelectFieldFormik
                                                id="penaltyType"
                                                name="penaltyType"
                                                label="Penalidade"
                                                options={penaltyOptions}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            md={3}
                                            xs={12}
                                            hidden={
                                                values.penaltyType ===
                                                    PenaltyType.STOP_AND_GO ||
                                                values.penaltyType ===
                                                    PenaltyType.ADD_TIME_TO_END
                                            }
                                        />
                                        <Grid
                                            item
                                            md={3}
                                            xs={12}
                                            hidden={
                                                values.penaltyType !==
                                                PenaltyType.STOP_AND_GO
                                            }
                                        >
                                            <InputFieldFormik
                                                id="stopAndGoTime"
                                                name="stopAndGoTime"
                                                label="Tempo do stop and go(segundos)"
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            md={3}
                                            xs={12}
                                            hidden={
                                                values.penaltyType !==
                                                PenaltyType.ADD_TIME_TO_END
                                            }
                                        >
                                            <InputFieldFormik
                                                id="addTimeToEnd"
                                                name="addTimeToEnd"
                                                label="Tempo de acréscimo(segundos)"
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item md={3} xs={12}>
                                            <InputFieldFormik
                                                id="penaltyPoints"
                                                name="penaltyPoints"
                                                label="Pontos de punição"
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item md={3} xs={12}>
                                            <InputFieldFormik
                                                id="penaltyChampPoints"
                                                name="penaltyChampPoints"
                                                label="Desconto de pontos no campeonato"
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item md={3} xs={12}>
                                            <InputFieldFormik
                                                id="penaltyPointsAppliedAfter"
                                                name="penaltyPointsAppliedAfter"
                                                label="Aplicar penalidade de pontos após"
                                                helperText={
                                                    values.penaltyPointsAppliedAfter >
                                                    1
                                                        ? "vezes"
                                                        : "vez"
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <InputFieldFormik
                                                id="description"
                                                name="description"
                                                label="Descrição"
                                                multiline
                                                rows={3}
                                                helperText={`Max.: ${values.description.length} / 200`}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box
                                                display="flex"
                                                flexDirection="row-reverse"
                                                justifyContent=""
                                            >
                                                <SubmitButton
                                                    style={{
                                                        marginLeft: 5,
                                                    }}
                                                />
                                                <Button
                                                    onClick={() =>
                                                        history.push(
                                                            "/infractions"
                                                        )
                                                    }
                                                >
                                                    Voltar
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </BoxCard>
                </Grid>
            </Grid>
        </PageLayout>
    );
};

export { InfractionForm };
